import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { STORAGE_KEYS } from "../utils/constants";
import { api, showNotification, NOTIFICATION_TYPES } from "../utils/apiClient";

const SuccessAnimation = ({ message }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
    <div className="bg-[#1C1C1C] rounded-3xl p-8 max-w-sm mx-4 shadow-2xl border border-white/10 animate-scale-bounce">
      <div className="text-center">
        <div className="relative mx-auto w-24 h-24 mb-6">
          <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-600 rounded-full animate-pulse-slow" />
          <div className="absolute inset-2 bg-[#1C1C1C] rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path className="animate-check-draw" strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" strokeDasharray="30" strokeDashoffset="30" />
            </svg>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2 animate-slide-up">Success!</h3>
        <p className="text-gray-400 animate-slide-up-delay">{message}</p>
      </div>
    </div>
  </div>
);

const RedirectingAnimation = ({ message = "Redirecting..." }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
    <div className="bg-[#1C1C1C] rounded-3xl p-8 max-w-sm mx-4 shadow-2xl border border-white/10 animate-scale-bounce">
      <div className="text-center">
        <div className="relative mx-auto w-24 h-24 mb-6">
          <div className="absolute inset-0">
            <div className="w-full h-full border-4 border-[#FF6B35]/20 rounded-full" />
            <div className="absolute inset-0 w-full h-full border-4 border-[#FF6B35] rounded-full border-t-transparent animate-spin-slow" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-[#FF6B35]/20 rounded-full animate-pulse" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">{message}</h3>
        <p className="text-gray-400">Please wait a moment...</p>
        <div className="flex justify-center space-x-2 mt-4">
          {[0, 150, 300].map((delay) => (
            <div key={delay} className="w-2 h-2 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const OTPInput = ({ length = 4, onComplete, loading = false }) => {
  const inputRef = useRef([]);
  const [otp, setOTP] = useState(Array(length).fill(""));

  useEffect(() => { inputRef.current[0]?.focus(); }, []);

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);
    if (value && index < length - 1) inputRef.current[index + 1]?.focus();
    if (!value && index > 0) inputRef.current[index - 1]?.focus();
    if (newOTP.every((d) => d !== "") && newOTP.join("").length === length) {
      if (!loading) onComplete(newOTP.join(""));
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) inputRef.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text/plain").slice(0, length);
    if (!/^\d+$/.test(pasted)) return;
    const newOTP = Array(length).fill("");
    pasted.split("").forEach((d, i) => { if (i < length) newOTP[i] = d; });
    setOTP(newOTP);
    inputRef.current[Math.min(pasted.length - 1, length - 1)]?.focus();
    if (newOTP.join("").length === length) onComplete(newOTP.join(""));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-center gap-3">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            value={otp[index]}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            ref={(ref) => (inputRef.current[index] = ref)}
            disabled={loading}
            className={`w-14 h-14 text-center text-xl font-bold border-2 rounded-xl bg-[#1C1C1C] text-white
              ${otp[index] ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-white/10"}
              focus:border-[#FF6B35] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20
              disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
            placeholder="•"
          />
        ))}
      </div>

      <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-green-500 flex-shrink-0">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.555 4.116 1.529 5.845L.057 23.428a.5.5 0 00.609.61l5.703-1.494A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.93 0-3.74-.524-5.29-1.432l-.38-.222-3.384.887.902-3.296-.246-.395A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
          </svg>
          <p className="text-sm text-green-400">Check your <strong>WhatsApp</strong> for the OTP</p>
        </div>
      </div>
    </div>
  );
};

const inputClass = (hasError) =>
  `block w-full py-3 px-3 bg-[#111111] border rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 transition-all text-sm ${
    hasError
      ? "border-red-500/40 focus:border-red-500 focus:ring-red-500/20"
      : "border-white/10 focus:border-[#FF6B35] focus:ring-[#FF6B35]/20"
  }`;

const Register = () => {
  const [step, setStep] = useState("registration");
  const [formData, setFormData] = useState({ name: "", phone: "", altphone: "", email: "", address: "" });
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [developmentMode, setDevelopmentMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showOtpSuccess, setShowOtpSuccess] = useState(false);
  const [showRegisterSuccess, setShowRegisterSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (location.state?.phoneNumber) setFormData((prev) => ({ ...prev, phone: String(location.state.phoneNumber) }));
    if (location.state?.returnUrl && !location.search.includes("returnUrl")) {
      const newUrl = `${location.pathname}?returnUrl=${encodeURIComponent(location.state.returnUrl)}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, [location.state]);

  useEffect(() => {
    let timer;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const validateField = (fieldName, value) => {
    const newErrors = { ...validationErrors };
    switch (fieldName) {
      case "name":
        if (!value.trim()) newErrors.name = "Full name is required";
        else if (value.trim().length < 2) newErrors.name = "Name must be at least 2 characters";
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) newErrors.name = "Name can only contain letters and spaces";
        else delete newErrors.name;
        break;
      case "email":
        if (!value.trim()) newErrors.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) newErrors.email = "Please enter a valid email";
        else delete newErrors.email;
        break;
      case "phone":
        if (!value.trim()) newErrors.phone = "Phone number is required";
        else if (!/^[6-9]\d{9}$/.test(value.trim())) newErrors.phone = "Phone must be 10 digits starting with 6-9";
        else delete newErrors.phone;
        break;
      case "altphone":
        if (!value.trim()) newErrors.altphone = "Alternate phone number is required";
        else if (!/^[6-9]\d{9}$/.test(value.trim())) newErrors.altphone = "Alt phone must be 10 digits starting with 6-9";
        else delete newErrors.altphone;
        break;
      case "address":
        if (!value.trim()) newErrors.address = "Address is required";
        else if (value.trim().length < 10) newErrors.address = "Address should be minimum 10 characters";
        else delete newErrors.address;
        break;
      default: break;
    }
    setValidationErrors(newErrors);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Full name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.phone.trim()) errors.phone = "Phone number is required";
    if (!formData.altphone.trim()) errors.altphone = "Alternate phone number is required";
    if (!formData.address.trim()) errors.address = "Address is required";
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) errors.email = "Please enter a valid email";
    if (formData.phone.trim() && !/^[6-9]\d{9}$/.test(formData.phone.trim())) errors.phone = "Phone must be 10 digits starting with 6-9";
    if (formData.altphone.trim() && !/^[6-9]\d{9}$/.test(formData.altphone.trim())) errors.altphone = "Alt phone must be 10 digits starting with 6-9";
    if (formData.address.trim() && formData.address.trim().length < 10) errors.address = "Address should be minimum 10 characters";
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) { setError("Please fix the errors below and try again."); return false; }
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === "phone" || name === "altphone") processedValue = value.replace(/\D/g, "").slice(0, 10);
    if (name === "email") processedValue = value.toLowerCase().trim();
    if (name === "name") processedValue = value.replace(/[^a-zA-Z\s]/g, "");
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    validateField(name, processedValue);
    if (error) setError("");
  };

const handleInitialSubmit = async (e) => {
  e.preventDefault();

  setError("");
  setSuccess("");

  if (!validateForm()) return;

  setLoading(true);

  try {
    const result = await api.post(
      "/user/register/send-otp",
      {
        phone: formData.phone.trim(),
      }
    );

    console.log("REGISTER RESULT:", result);

    // IMPORTANT: apiClient returns success:false instead of throwing
    if (!result.success) {
      const msg =
        result.error ||
        result.details?.message ||
        "Failed to send OTP";

      setError(msg);

      showNotification(
        msg,
        NOTIFICATION_TYPES.ERROR
      );

      // Optional redirect to login
      if (result.status === 409) {
        setTimeout(() => {
          navigate("/login", {
            state: {
              phoneNumber: formData.phone.trim(),
            },
          });
        }, 3000);
      }

      return;
    }

    // SUCCESS RESPONSE
    const responseData = result.data;

    setDevelopmentMode(
      Boolean(responseData?.developmentMode)
    );

    setShowOtpSuccess(true);

    showNotification(
      responseData?.message || "OTP sent successfully",
      NOTIFICATION_TYPES.SUCCESS
    );

    setTimeout(() => {
      setShowOtpSuccess(false);

      setSuccess(
        responseData?.message || "OTP sent successfully"
      );

      setStep("otp-verification");
      setCountdown(30);

      window.scrollTo(0, 0);
    }, 1500);

  } catch (err) {
    console.log("REGISTER ERROR:", err);

    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to send OTP";

    setError(msg);

    showNotification(
      msg,
      NOTIFICATION_TYPES.ERROR
    );

  } finally {
    setLoading(false);
  }
};

  const handleVerifyOTP = async (otpCode) => {
    if (!otpCode || otpCode.length !== 4) { setError("Please enter a valid 4-digit OTP"); return; }
    setOtpLoading(true);
    setError("");
    try {
      const payload = {
        name: (formData.name ?? "").trim(),
        phone: (formData.phone ?? "").trim(),
        otp: (otpCode ?? "").trim(),
        altphone: (formData.altphone ?? "").trim(),
        email: (formData.email ?? "").trim(),
        address: (formData.address ?? "").trim(),
      };
      const result = await api.post("/user/register/verify-otp", payload);
  const token =
  result?.data?.token;

const user =
  result?.data?.user;

if (!token) {
  setError(
    "Registration error: no token received. Please try again."
  );

  return;
}


// ==========================================
// SAVE JWT FOR WEBSITE
// ==========================================
localStorage.setItem(
  STORAGE_KEYS.TOKEN,
  token
);


// ==========================================
// SEND JWT TO ANDROID WEBVIEW
// ==========================================
if (
  typeof window !== "undefined" &&
  window.ReactNativeWebView &&
  typeof window.ReactNativeWebView.postMessage ===
    "function"
) {
  console.log(
    "📱 SENDING REGISTER AUTH TOKEN TO ANDROID"
  );

  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      type: "AUTH_TOKEN",
      token: token,
    })
  );
} else {
  console.log(
    "🌐 NORMAL BROWSER - NOT ANDROID WEBVIEW"
  );
}


// ==========================================
// SAVE USER
// ==========================================
if (user) {
  localStorage.setItem(
    STORAGE_KEYS.USER,
    JSON.stringify(user)
  );
}
      setShowRegisterSuccess(true);
      showNotification(result.data?.message || "Registration successful!", NOTIFICATION_TYPES.SUCCESS);
      setTimeout(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = urlParams.get("returnUrl");
        window.location.href = returnUrl ? decodeURIComponent(returnUrl) : "/";
      }, 2000);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Verification failed";
      setError(msg);
      showNotification(msg, NOTIFICATION_TYPES.ERROR);
    } finally {
      setOtpLoading(false);
    }
  };

const handleResendOTP = async () => {
  setOtpLoading(true);
  setError("");
  setSuccess("");

  try {
    const result = await api.post(
      "/user/register/send-otp",
      {
        phone: formData.phone.trim(),
      }
    );

    const responseData = result?.data || result;

    if (!responseData) {
      throw new Error("Failed to resend OTP");
    }

    setSuccess(
      responseData?.message || "OTP resent successfully"
    );

    setCountdown(30);

    showNotification(
      responseData?.message || "OTP resent successfully",
      NOTIFICATION_TYPES.SUCCESS
    );

  } catch (err) {

    const status = err.response?.status;

    const msg =
      err.response?.data?.message ||
      err.message ||
      "Failed to resend OTP";

    if (status === 409) {
      setError(
        "This phone number is already registered. Please login."
      );

      showNotification(
        "Phone number is already registered. Please login.",
        NOTIFICATION_TYPES.ERROR
      );

      return;
    }

    setError(msg);

    showNotification(
      msg,
      NOTIFICATION_TYPES.ERROR
    );

  } finally {
    setOtpLoading(false);
  }
};

  const animations = `
    @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes scale-bounce {
      0% { transform: scale(0.3); opacity: 0; }
      50% { transform: scale(1.05); }
      70% { transform: scale(0.95); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes check-draw { from { stroke-dashoffset: 30; } to { stroke-dashoffset: 0; } }
    @keyframes pulse-slow { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.05); } }
    @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes slide-up-delay { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-8px); } 75% { transform: translateX(8px); } }
    .animate-fade-in { animation: fade-in 0.3s ease-out; }
    .animate-scale-bounce { animation: scale-bounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55); }
    .animate-check-draw { animation: check-draw 0.6s ease-out 0.3s forwards; }
    .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
    .animate-slide-up { animation: slide-up 0.4s ease-out 0.5s both; }
    .animate-slide-up-delay { animation: slide-up-delay 0.4s ease-out 0.6s both; }
    .animate-spin-slow { animation: spin-slow 1.5s linear infinite; }
    .animate-shake { animation: shake 0.4s ease-in-out; }
  `;

  // OTP verification view
  if (step === "otp-verification") {
    return (
      <>
        {showRegisterSuccess && <SuccessAnimation message="Registration successful! Welcome to OkMechanic!" />}
        <style>{animations}</style>

        <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center py-12 px-4 sm:px-6">
          <div
            className="fixed inset-0 opacity-[0.03] pointer-events-none"
            style={{ backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`, backgroundSize: '60px 60px' }}
          />

          <div className="relative w-full max-w-md">
            <div className="text-center mb-6">
              <Link to="/" className="inline-flex items-center gap-2 mb-6 group">
                <img src="/logo.png" alt="OkMechanic" className="h-10 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
                <span className="text-white font-bold text-xl group-hover:text-[#FF6B35] transition-colors">Ok<span className="text-[#FF6B35]">Mechanic</span></span>
              </Link>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF6B35]/10 rounded-2xl mb-4 border border-[#FF6B35]/20">
                <ShieldCheckIcon className="h-7 w-7 text-[#FF6B35]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">Verify Your Phone</h2>
              <p className="text-gray-500 mb-1 text-sm">Enter the 4-digit code sent to</p>
              <p className="text-[#FF6B35] font-bold text-lg">+91 {formData.phone}</p>
              <p className="text-sm text-green-400 mt-2 flex items-center justify-center gap-1.5">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-green-500 flex-shrink-0">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.555 4.116 1.529 5.845L.057 23.428a.5.5 0 00.609.61l5.703-1.494A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.93 0-3.74-.524-5.29-1.432l-.38-.222-3.384.887.902-3.296-.246-.395A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                </svg>
                OTP sent to your WhatsApp
              </p>
            </div>

            <div className="bg-[#1A1A1A] rounded-2xl p-6 sm:p-8 border border-white/[0.06]">
              <div className="space-y-5">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl flex items-start animate-shake">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}
                {success && (
                  <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded-xl flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span className="text-sm">{success}</span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-4 text-center">Enter Verification Code</label>
                  <OTPInput length={4} onComplete={handleVerifyOTP} loading={otpLoading} />
                </div>

                <div className="flex justify-center">
                  {countdown > 0 ? (
                    <div className="flex items-center text-gray-500 text-sm">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      <span>Resend OTP in {countdown}s</span>
                    </div>
                  ) : (
                    <button type="button" onClick={handleResendOTP} disabled={otpLoading} className="text-[#FF6B35] hover:text-[#E85520] font-semibold disabled:opacity-50 transition-colors text-sm">
                      {otpLoading ? "Sending..." : "Resend OTP"}
                    </button>
                  )}
                </div>

                <div className="text-center pt-4 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => { setStep("registration"); setError(""); setSuccess(""); }}
                    className="flex items-center justify-center text-gray-500 hover:text-gray-300 font-medium mx-auto transition-colors text-sm"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Change Phone Number
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Registration form view
  return (
    <>
      {showOtpSuccess && <SuccessAnimation message="OTP sent to your WhatsApp!" />}
      {redirecting && <RedirectingAnimation message="Redirecting..." />}
      <style>{animations}</style>

      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center py-12 px-4 sm:px-6">
        <div
          className="fixed inset-0 opacity-[0.03] pointer-events-none"
          style={{ backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`, backgroundSize: '60px 60px' }}
        />

        <div className="relative w-full max-w-2xl">

          {/* Brand */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-5 group">
              <img src="/logo.png" alt="OkMechanic" className="h-10 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="text-white font-bold text-xl group-hover:text-[#FF6B35] transition-colors">Ok<span className="text-[#FF6B35]">Mechanic</span></span>
            </Link>
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF6B35]/10 rounded-2xl mb-4 border border-[#FF6B35]/20">
              <UserIcon className="h-7 w-7 text-[#FF6B35]" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">Create Your Account</h2>
            <p className="text-sm text-gray-500">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-[#FF6B35] hover:text-[#E85520] transition-colors">Sign in here</Link>
            </p>
          </div>

          <div className="bg-[#1A1A1A] rounded-2xl p-6 sm:p-8 border border-white/[0.06]">
            <form onSubmit={handleInitialSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl flex items-start animate-shake">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
              {success && (
                <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded-xl flex items-center">
                  <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span className="text-sm">{success}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {/* Full Name */}
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">
                    Full Name <span className="text-[#FF6B35]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="h-4 w-4 text-gray-500" />
                    </div>
                    <input
                      id="name" name="name" type="text" value={formData.name} onChange={handleChange} required
                      className={`${inputClass(validationErrors.name)} pl-9`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {validationErrors.name && <p className="text-xs text-red-400 mt-1">{validationErrors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                    Email <span className="text-[#FF6B35]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-4 w-4 text-gray-500" />
                    </div>
                    <input
                      id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
                      className={`${inputClass(validationErrors.email)} pl-9`}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  {validationErrors.email && <p className="text-xs text-red-400 mt-1">{validationErrors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-300 mb-2">
                    WhatsApp Number <span className="text-[#FF6B35]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <PhoneIcon className="h-4 w-4 text-gray-500" />
                    </div>
                    <div className="absolute inset-y-0 left-9 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-xs font-medium">+91</span>
                    </div>
                    <input
                      id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} maxLength={10} required
                      className={`${inputClass(validationErrors.phone)} pl-[3.5rem]`}
                      placeholder="9876543210"
                    />
                  </div>
                  {validationErrors.phone && <p className="text-xs text-red-400 mt-1">{validationErrors.phone}</p>}
                </div>

                {/* Alt Phone */}
                <div>
                  <label htmlFor="altphone" className="block text-sm font-semibold text-gray-300 mb-2">
                    Alternate Phone <span className="text-[#FF6B35]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DevicePhoneMobileIcon className="h-4 w-4 text-gray-500" />
                    </div>
                    <div className="absolute inset-y-0 left-9 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-xs font-medium">+91</span>
                    </div>
                    <input
                      id="altphone" name="altphone" type="tel" value={formData.altphone} onChange={handleChange} maxLength={10} required
                      className={`${inputClass(validationErrors.altphone)} pl-[3.5rem]`}
                      placeholder="9876543210"
                    />
                  </div>
                  {validationErrors.altphone && <p className="text-xs text-red-400 mt-1">{validationErrors.altphone}</p>}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-300 mb-2">
                    Address <span className="text-[#FF6B35]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <MapPinIcon className="h-4 w-4 text-gray-500" />
                    </div>
                    <textarea
                      id="address" name="address" rows={3} value={formData.address} onChange={handleChange} required
                      className={`${inputClass(validationErrors.address)} pl-9 resize-none`}
                      placeholder="Enter your complete address"
                    />
                  </div>
                  {validationErrors.address && <p className="text-xs text-red-400 mt-1">{validationErrors.address}</p>}
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || Object.keys(validationErrors).length > 0}
                  className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl text-base font-bold text-white bg-[#FF6B35] hover:bg-[#E85520] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-[#FF6B35]/20 hover:shadow-[#FF6B35]/30 hover:-translate-y-0.5"
                >
                  {loading ? (
                    <>
                      <ArrowPathIcon className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <ShieldCheckIcon className="h-5 w-5 mr-2" />
                      Register with OTP
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
